import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Col, Row, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import useApi from '../../../hooks/useApi'
import { ROUTES } from '../../../constants/routes'
import CharityVerificationRequirementsModal from './CharityVerificationRequirementsModal'

const STATUS_VARIANTS = {
  not_created: 'secondary',
  pending: 'warning',
  restricted: 'warning',
  active: 'success',
  disabled: 'danger',
}

export default function StripeConnectSection({ charityId }) {
  const { t } = useTranslation()
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState(null)
  const [showRequirementsModal, setShowRequirementsModal] = useState(false)
  const [stripeStatus, setStripeStatus] = useState(null)

  const {
    createCharityStripeAccount,
    getCharityStripeAccountStatus,
    getCharityStripeOnboardingLink,
  } = useApi()

  useEffect(() => {
    if (!charityId) return

    let cancelled = false

    const fetchStatus = async () => {
      setLoadingStatus(true)
      setError(null)
      try {
        const response = await getCharityStripeAccountStatus(charityId)
        if (cancelled) return

        if (response?.data) {
          setStripeStatus(response.data)
        } else if (response?.error) {
          setError(
            response.error?.response?.data?.error || 'Failed to fetch status',
          )
        }
      } catch (err) {
        if (cancelled) return
        setError(err?.message || 'Failed to fetch Stripe status')
      } finally {
        if (!cancelled) {
          setLoadingStatus(false)
        }
      }
    }

    fetchStatus()

    return () => {
      cancelled = true
    }
  }, [charityId, getCharityStripeAccountStatus])

  // Don't render anything if no charityId provided
  if (!charityId) {
    return null
  }

  // Show loading state during initial fetch
  const isInitialLoading = stripeStatus === null && loadingStatus

  // Unified check for whether a Stripe account exists
  const isNotCreated = !stripeStatus?.has_stripe_account
  const status = stripeStatus?.stripe_account_status || 'not_created'
  const isFullyConnected = status === 'active' && stripeStatus?.stripe_payouts_enabled

  const handleConnectStripe = useCallback(async () => {
    setConnecting(true)
    setError(null)

    try {
      // Create account if it doesn't exist
      if (isNotCreated) {
        const createResponse = await createCharityStripeAccount(charityId)
        if (createResponse?.error) {
          throw new Error(
            createResponse.error?.response?.data?.error ||
              'Failed to create Stripe account',
          )
        }
      }

      // Get onboarding link
      const returnUrl = `${window.location.origin}${ROUTES.CHARITY_DASHBOARD}?stripe_return=true&charity_id=${charityId}`
      const refreshUrl = `${window.location.origin}${ROUTES.CHARITY_DASHBOARD}?stripe_refresh=true&charity_id=${charityId}`

      const linkResponse = await getCharityStripeOnboardingLink(
        charityId,
        returnUrl,
        refreshUrl,
      )

      if (linkResponse?.data?.onboarding_url) {
        // Redirect to Stripe onboarding
        window.location.href = linkResponse.data.onboarding_url
      } else {
        throw new Error(
          linkResponse?.error?.response?.data?.error ||
            'Failed to get onboarding link',
        )
      }
    } catch (err) {
      setError(err?.message || 'Failed to connect Stripe')
    } finally {
      // Reset connecting state if redirect doesn't happen (e.g., blocked by browser)
      setConnecting(false)
    }
  }, [
    charityId,
    isNotCreated,
    createCharityStripeAccount,
    getCharityStripeOnboardingLink,
  ])

  const getStatusBadge = () => {
    const variant = STATUS_VARIANTS[status] || STATUS_VARIANTS.not_created
    return (
      <span className={`badge bg-${variant}`}>
        {t(`charities.dashboard.stripe.status.${status}`)}
      </span>
    )
  }

  const getActionButton = () => {
    if (isFullyConnected) {
      return null // Fully connected, no action needed
    }

    if (isNotCreated) {
      return (
        <Button
          variant="primary"
          onClick={() => setShowRequirementsModal(true)}
          disabled={connecting}
        >
          {connecting ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t('charities.dashboard.stripe.connectingButton')}
            </>
          ) : (
            t('charities.dashboard.stripe.connectButton')
          )}
        </Button>
      )
    }

    // Account exists but needs more info or is restricted
    return (
      <Button
        variant="warning"
        onClick={() => setShowRequirementsModal(true)}
        disabled={connecting}
      >
        {connecting ? (
          <>
            <Spinner size="sm" className="me-2" />
            {t('charities.dashboard.stripe.loadingButton')}
          </>
        ) : (
          t('charities.dashboard.stripe.completeSetupButton')
        )}
      </Button>
    )
  }

  return (
    <div className="container mt-4">
      <Row className="align-items-center">
        <Col>
          <h3 className="text-start mb-0">
            {t('charities.dashboard.stripe.title')}
          </h3>
        </Col>
      </Row>

      {error && (
        <Row className="mt-3">
          <Col>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {isInitialLoading ? (
        <Row className="mt-3 p-3 bg-light rounded">
          <Col className="d-flex align-items-center justify-content-center">
            <Spinner size="sm" className="me-2" />
            <span className="text-muted">
              {t('charities.dashboard.stripe.loadingStatus')}
            </span>
          </Col>
        </Row>
      ) : (
        <>
          <Row className="mt-3 p-3 bg-light rounded">
            <Col md={8}>
              <div className="d-flex align-items-center gap-3">
                <div>
                  <strong>{t('charities.dashboard.stripe.connect')}</strong>
                  <p className="text-muted mb-0 small">
                    {stripeStatus?.stripe_payouts_enabled
                      ? t('charities.dashboard.stripe.connectedDescription')
                      : t('charities.dashboard.stripe.notConnectedDescription')}
                  </p>
                </div>
              </div>
            </Col>
            <Col
              md={4}
              className="d-flex align-items-center justify-content-end gap-2"
            >
              {getStatusBadge()}
              {getActionButton()}
            </Col>
          </Row>

          {stripeStatus?.has_stripe_account && (
            <Row className="mt-2">
              <Col>
                <small className="text-muted">
                  <strong>
                    {t('charities.dashboard.stripe.payoutsEnabled')}
                  </strong>{' '}
                  {stripeStatus.stripe_payouts_enabled
                    ? t('charities.dashboard.stripe.yes')
                    : t('charities.dashboard.stripe.no')}
                  {' | '}
                  <strong>
                    {t('charities.dashboard.stripe.detailsSubmitted')}
                  </strong>{' '}
                  {stripeStatus.stripe_details_submitted
                    ? t('charities.dashboard.stripe.yes')
                    : t('charities.dashboard.stripe.no')}
                </small>
              </Col>
            </Row>
          )}
        </>
      )}
      <CharityVerificationRequirementsModal
        show={showRequirementsModal}
        onHide={() => setShowRequirementsModal(false)}
        onContinue={() => {
          setShowRequirementsModal(false)
          handleConnectStripe()
        }}
        continuing={connecting}
      />
    </div>
  )
}

StripeConnectSection.propTypes = {
  charityId: PropTypes.number.isRequired,
}
